import {
	MouseEvent,
	PropsWithChildren,
	ReactElement,
	useContext
} from "react";
import classNames from "classnames";
import {
	setTrackingId,
	generateTrackingId,
	isNotNil
} from "client/Misc/utils";
import { ValidationError } from "../ValidationError/ValidationError";
import styles from "./ReorderableList.module.less";
import { ReorderableListItemBase } from "./ReorderableListItemBase";
import { ReorderableListDragIndicator } from "./ReorderableListDragIndicator";
import { ReorderableListDragHandle } from "./ReorderableListDragHandle";
import { Coordinate } from "client/Misc/commonTypes";
import { ReorderableListItemContext } from "./ReorderableListItemContext";
import { useDraggable, useIsTouchDevice } from "client/Hooks";

export type ReorderableListItemMouseInteractionConfiguration<TDraggableMode extends string> = {
	draggableMode?: TDraggableMode;
	onContextMenu?: (coordinate: Coordinate) => void;
};

export type ReorderableListItemDraggableMode = "handle" | "entire-row";

export type ReorderableListItemProps = {
	actions?: ReactElement;
	dontEllipseText?: boolean;
	isContentInteractable?: boolean;
	validationMessage?: string;
	trackingId?: string;
	noSpaceAroundContent?: boolean;
	isActive?: boolean;
	mouseInteraction?: ReorderableListItemMouseInteractionConfiguration<ReorderableListItemDraggableMode>;
};

export const ReorderableListRowItem = (props: PropsWithChildren<ReorderableListItemProps>): ReactElement => {
	const {
		actions,
		dontEllipseText = false,
		isContentInteractable = false,
		trackingId,
		validationMessage,
		children,
		noSpaceAroundContent = false,
		isActive,
		mouseInteraction
	} = props;

	const mouseDraggableMode: ReorderableListItemDraggableMode = mouseInteraction?.draggableMode
		?? "entire-row";

	const onMouseContextMenu: ((coordinate: Coordinate) => void) | undefined = mouseInteraction
		?.onContextMenu;

	const {
		isReorderEnabled,
		dragMove,
		dragStart,
		dragStop
	} = useContext(ReorderableListItemContext);

	const isTouchDevice: boolean = useIsTouchDevice();

	const {
		ref: draggableRef
	} = useDraggable({
		onStart: dragStart,
		onMove: dragMove,
		onStop: dragStop,
		isEnabled: isReorderEnabled
	});

	const isHoverable: boolean = isContentInteractable
		|| isReorderEnabled
		|| (isNotNil(onMouseContextMenu) && !isTouchDevice);

	const contextMenuHandler: ((args: MouseEvent) => void) | undefined = !isTouchDevice
		&& isNotNil(onMouseContextMenu)
			? (args: MouseEvent): void => {
				args.preventDefault();

				onMouseContextMenu({
					x: args.clientX,
					y: args.clientY
				});
			}
			: undefined;

	return (
		<ReorderableListItemBase
			trackingId={trackingId}
		>
			<ReorderableListDragIndicator>
				<div
					{...setTrackingId(trackingId, "__content")}
					ref={
						!isTouchDevice && mouseDraggableMode === "entire-row"
							? draggableRef
							: undefined
					}
					className={classNames(
						styles.rowItem,
						{
							[styles.rowItem_hoverable]: isHoverable,
							[styles.rowItem_active]: isActive
						}
					)}
				>
					<ReorderableListDragHandle
						isReorderEnabled={isReorderEnabled}
						ref={
							isTouchDevice || mouseDraggableMode === "handle"
								? draggableRef
								: undefined
						}
					/>
					<div className={
						classNames(
							styles.rowItemContent,
							{
								[styles.rowItemContent_ellipsed]: !dontEllipseText,
								[styles.rowItemContent_noSpaceAround]: noSpaceAroundContent
							}
						)}
						onContextMenu={contextMenuHandler}
					>
						{children}
					</div>
					<div className={styles.rowItemActions}>
						{actions}
					</div>
				</div>
			</ReorderableListDragIndicator>
			<ValidationError
				message={validationMessage}
				trackingId={generateTrackingId(trackingId, "__validation-error")}
			/>
		</ReorderableListItemBase>
	);
};

ReorderableListRowItem.displayName = "ReorderableList.RowItem";
